import type { GridViewState } from '@stageplottter/core';
import { filterRows, groupRows, initialGridViewState, sortRows } from '@stageplottter/core';
import type { GridColumn, GridValue } from './types';
import { formatCell } from './values';

/** A rendered line of the grid body: a group header or a data row. */
export type DisplayLine<R> =
  { type: 'group'; key: string; count: number } | { type: 'row'; row: R; index: number };

export interface GridView<R> {
  /** Data rows in display order (navigation indices). */
  rows: R[];
  /** Data rows with group headers interleaved (render order). */
  lines: DisplayLine<R>[];
}

function sortable(v: GridValue): unknown {
  return Array.isArray(v) ? v.join(', ') : v;
}

/**
 * Filters (contains, case-insensitive, on the display text), sorts (on the raw value) and groups
 * (on the display text) rows with the core grid helpers.
 */
export function applyGridView<R>(
  rows: readonly R[],
  columns: readonly GridColumn<R>[],
  state: GridViewState,
): GridView<R> {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const text = (row: R, key: string): unknown => {
    const col = byKey.get(key);
    return col === undefined ? '' : formatCell(col, row);
  };
  const raw = (row: R, key: string): unknown => {
    const col = byKey.get(key);
    return col === undefined ? '' : sortable(col.value(row));
  };
  const sorted = sortRows(filterRows(rows, state.filters, text), state.sort, raw);
  if (state.groupBy === null || !byKey.has(state.groupBy)) {
    return { rows: sorted, lines: sorted.map((row, index) => ({ type: 'row', row, index })) };
  }
  const groups = groupRows(sorted, state.groupBy, text);
  const out: R[] = [];
  const lines: DisplayLine<R>[] = [];
  for (const g of groups) {
    lines.push({ type: 'group', key: g.key, count: g.rows.length });
    for (const row of g.rows) {
      lines.push({ type: 'row', row, index: out.length });
      out.push(row);
    }
  }
  return { rows: out, lines };
}

/**
 * Session-only view state per table id (decision Q14: kept while the app is open, never
 * persisted, so switching panes keeps sorting / filters / grouping).
 */
const sessionViews = new Map<string, GridViewState>();

export function sessionView(id: string): GridViewState {
  return sessionViews.get(id) ?? initialGridViewState();
}

export function saveSessionView(id: string, state: GridViewState): void {
  sessionViews.set(id, { ...state, selection: null });
}

export function setFilter(state: GridViewState, key: string, query: string): GridViewState {
  const filters = { ...state.filters };
  if (query === '') delete filters[key];
  else filters[key] = query;
  return { ...state, filters };
}

export function setGroupBy(state: GridViewState, key: string | null): GridViewState {
  return { ...state, groupBy: key };
}

export function sortDirection(
  state: GridViewState,
  key: string,
): 'ascending' | 'descending' | 'none' {
  const s = state.sort.find((x) => x.key === key);
  return s === undefined ? 'none' : s.dir === 'asc' ? 'ascending' : 'descending';
}

/** Session-only visibility of optional columns (custom properties) per table id. */
const sessionColumns = new Map<string, readonly string[]>();

export function visibleOptional(id: string): readonly string[] {
  return sessionColumns.get(id) ?? [];
}

export function toggleOptional(id: string, key: string): readonly string[] {
  const cur = visibleOptional(id);
  const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
  sessionColumns.set(id, next);
  return next;
}
